import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { FaHeart, FaMapMarkerAlt, FaStar, FaChevronLeft, FaChevronRight, FaEye } from 'react-icons/fa';
import type { Restaurant } from '../../Manager/restaurant.types';
import { formatAddressBadge } from './services';
import SaveButton from '../../Components/SaveButton';

interface RestaurantCardProps {
  restaurant: Restaurant;
  onCardClick: (id: string, name: string) => void;
}
const generateUrl = (url: string) => {
  // console.log(`${url}?w=200&h=200&fit=crop&q=80`);
  return `${url}?w=200&h=200&fit=crop&q=80`
}
const RestaurantCard: React.FC<RestaurantCardProps> = ({ 
  restaurant,
  onCardClick 
}) => {
  const [clicked, setClicked] = useState(false);

  const formatCurrency = (amount: number) => {
    const formatted = new Intl.NumberFormat(navigator.language ||"en-KE", {
      style: "currency",
      currency: "KES",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);

    return formatted.replace("KES", "Ksh");
  };

  const randomLabels = useMemo(() => {
    const allLabels = [
      ...(restaurant.typeOfFood ?? []),
      ...(restaurant.experience ?? []),
      // ...(restaurant.tags ?? []),
      ...(restaurant.cuisine ?? []),
      // ...(restaurant.specialFeatures ?? []),
    ].filter(Boolean);

    if (allLabels.length === 0) return [];

    // Shuffle once, then pick 3
    return allLabels
      .sort(() => 0.5 - Math.random())
      .slice(0, 3);
  }, [restaurant._id]);

  const metadataRating = typeof restaurant.googleMetadata?.rating === 'number'
    ? restaurant.googleMetadata.rating
    : null;
  const overallRating = metadataRating ?? restaurant.rating.average ?? 0;


  return (
    <div 
      className="flex-shrink-0 w-60 bg-white overflow-hidden hover:opacity-90 transition-all duration-300 cursor-pointer"
      onClick={() => onCardClick(restaurant._id, restaurant.name)}
    >
      <div className="relative h-60 rounded-3xl bg-gray-200">
        <img 
          src={generateUrl(restaurant.gallery?.cover?.url) || null} 
          alt={restaurant.name}
          className="w-full h-full rounded-3xl object-cover shadow"
        />
      
        <div className="absolute top-1 left-0 right-0 px-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FaMapMarkerAlt className="text-white" size={12} />
            <span className="text-sm text-white font-medium">{formatAddressBadge(restaurant?.location?.area, restaurant?.location?.city)}</span>
          </div>          
          <SaveButton
            restaurantId={restaurant._id}
            stroke={"white"}
          />
        </div>
      </div>
      
      <div>
        <div className='flex justify-between items-center'>
          <h3 className="font-semibold text-[#222222] text-base truncate inline">
            {restaurant.name}
          </h3>
          <div className="flex items-center gap-1 pr-2">
            <FaStar className="text-[#222222]" size={12} />
            <span className="text-sm font-semibold text-[#222222]">{overallRating}</span>
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-800 p-0">
            From {`${formatCurrency(restaurant.priceRange.min)}` || '$$$'} pp
          </span>

          <div className="flex items-center text-sm text-gray-600 pr-2">
            <FaEye className="mr-1" size={12} />
            <span>{restaurant.viewCount}</span>
          </div>
        </div>
        <div className="text-xs text-gray-600 truncate">
          {randomLabels.join(', ')}
        </div>
      </div>
    </div>
  );
};

export default RestaurantCard